<script setup lang="ts">
definePageMeta({
  title: 'Resources',
  description: 'Printable materials from ICS; certifications, policies, catalogs, and other literature.',
  order: 5,
})

const config = useRuntimeConfig()

const route = useRoute()

const atResourcesRoot = computed(() => /\/resources\/?$/.test(route.path))

const { data: featuredResources } = await useFetch<DefaultResource[]>(
  '/api/items/resources',
  {
    key: 'page:resources:featured-resources',
    query: {
      filter: {
        _and: [
          {
            status: { _eq: 'published' },
          },
          {
            featured: { _eq: true },
          },
        ],
      },
    },
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)
</script>

<template>
  <PageRoot>
    <PageHead
      theme="dark"
      :at-root="atResourcesRoot"
    >
      <template #parallax>
        <EffectBgMountains
          :photo="3"
          color="blue"
        />
      </template>
    </PageHead>
    <PageBody>
      <BlockContent v-if="atResourcesRoot">
        <h2 class="subpage-title mb-6">
          Printable Materials from ICS
        </h2>
        <p class="text-lg mb-12">
          Browse our library of downloadable catalogs, certifications, and other documentation.
        </p>
        <div class="flex flex-wrap gap-y-12 f-gap-x-32-48">
          <template v-for="resource in featuredResources">
            <div
              v-if="resource"
              :key="resource.id"
              class="flex flex-wrap gap-x-6 gap-y-4"
            >
              <div class="group relative">
                <NuxtLink
                  :to="`${config.public.directusUrl}/assets/${resource.pdf}?download`"
                  class="media-[(pointer:coarse)]:pointer-events-none"
                >
                  <PdfThumb
                    :pdf="resource.pdf"
                    :alt="`Preview of the document: ${resource.name}`"
                    class="elevation-3"
                    thumb-class="max-size-100"
                    placeholder-class="h-100"
                  />
                </NuxtLink>
                <div class="text-white bg-icsblue/62 opacity-0 flex flex-col gap-y-2 pointer-events-none transition-opacity items-center absolute-inset justify-center z-1 group-hover:opacity-100">
                  <span class="i-carbon-download size-16" />
                  <span class="text-lg">Download PDF</span>
                </div>
              </div>
              <div class="max-w-64 space-y-4">
                <h3 class="text-2xl heading-strong leading-7 drop-shadow">
                  {{ resource.featured_name ?? resource.name }}
                </h3>
                <AButton
                  label="Download PDF"
                  color="cyan"
                  icon="i-carbon-download"
                  :to="`${config.public.directusUrl}/assets/${resource.pdf}?download`"
                />
                <div>
                  <NuxtLink
                    :to="`/resources/${resource.resource_type.toLowerCase()}`"
                    class="link"
                  >
                    See all {{ resource.resource_type }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </template>
        </div>
      </BlockContent>
      <NuxtPage />
    </PageBody>
  </PageRoot>
</template>
